import React, { useMemo, useCallback } from 'react'
import { observer } from 'mobx-react'
import makeStyles from '@mui/styles/makeStyles'
import { Typography, Stack, IconButton, Tooltip } from '@mui/material'
import AltRouteIcon from '@mui/icons-material/AltRoute'

import { extractPostalCodeFromAddress } from 'admin/utils/helper'
import { useStores } from 'admin/hooks'

import styles from './styles'

const useStyles = makeStyles(styles)

const IMAGES = {
  homePickupDriver: {
    default: '/images/homepickup-driver.png',
    black: '/images/homepickup-driver-black.svg',
  },
  outletDriver: {
    default: '/images/outlet-driver.png',
    black: '/images/outlet-driver-black.svg',
  },
  iconLaundry: {
    default: '/images/laundry-color.svg',
    black: '/images/laundry-icon-black.svg',
  },
  iconCurtain: {
    default: '/images/curtain-color.svg',
    black: '/images/curtain-icon-black.svg',
  },
  preferred: '/images/preferred-driver.png',
}

const BookingInfo = ({ data }) => {
  const classes = useStyles()

  return (
    data &&
    data.map((item) => (
      <Stack
        key={item.id}
        direction="row"
        alignItems="center"
        className={classes.label}
      >
        {item.icon && (
          <img
            src={item.icon}
            alt="icon"
            width="22px"
            height="22px"
          />
        )}
        <Typography
          variant={item.variant || 'body2'}
          className={classes.label}
        >
          {item.info}
        </Typography>
      </Stack>
    ))
  )
}

const DriverType = ({
  driver,
  color = 'default', // default / white
  label,
  labelVariant = 'body2',
  groupByDriver,
  tag,
  isPreferred,
  isTimeSheet,
  bookedList,
}) => {
  const classes = useStyles()
  const { notificationStore } = useStores()

  let src = IMAGES[driver.type][color]
  if (!src) src = IMAGES.homePickupDriver.default // default image

  const total = useMemo(() => {
    let totalCurtainTag = 0
    let totalOtherTag = 0
    let totalValue = 0
    let totalReserve = 0
    if (groupByDriver) {
      Object.entries(groupByDriver).forEach(([key, value]) => {
        if (driver.id === key) {
          const valueBooking = value.filter((item) => item.isReserve === false)
          totalValue = valueBooking.length
          totalReserve = value.filter((item) => item.isReserve === true).length

          // calculate at Timesheet tab only
          if (tag) {
            totalCurtainTag = valueBooking.filter((values) =>
              values.bookingTags.some(
                (item) =>
                  item?.tag?.name.includes('Sofa (Dismantling)') ||
                  item?.tag?.name.includes('Curtain (Dismantling)'),
              ),
            ).length
            totalOtherTag = totalValue - totalCurtainTag
          }
        }
      })
    }
    return {
      curtain: String(totalCurtainTag),
      other: `${String(totalOtherTag)} (${totalReserve})`,
    }
  }, [groupByDriver, driver, tag])

  const filterBooking = useMemo(() => {
    if (!bookedList) return []
    return bookedList.filter((item) => !['completed', 'failed'].includes(item.status))
  }, [bookedList])

  const isRouteDisabled = useMemo(() => !filterBooking.length, [filterBooking])

  const handleRouteAllLocations = useCallback((items) => {
    let url = ''
    const query = items
      .map((item) => {
        let extractPostalCode =
          extractPostalCodeFromAddress(item.address) || item.customer?.postalCode
        if (!extractPostalCode) {
          extractPostalCode = item.address || item.customer?.address
        }
        if (!extractPostalCode) {
          return notificationStore.setError('Postal code or address not found.')
        }
        return encodeURIComponent(`Singapore ${extractPostalCode}`)
      })
      .join('/')

    if (items.length !== 1) {
      url = `https://www.google.com/maps/dir/${query}`
    } else {
      url = `https://www.google.com/maps/dir/?api=1&travelmode=driving&dir_action=navigate&destination=${query}`
    }

    window.open(url, '_blank')
  }, [])

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row">
        <BookingInfo
          data={[
            {
              id: 1,
              icon: src,
              info: label || `${driver.code} - ${driver.name}`,
              variant: labelVariant,
            },
          ]}
        />
        {isPreferred && (
          <img
            src={IMAGES.preferred}
            alt="Preferred driver"
            width="20px"
            height="20px"
            className={classes.preferred}
          />
        )}
      </Stack>
      {groupByDriver && tag && (
        <Stack direction="row">
          {isTimeSheet && (
            <Tooltip
              title="Route all locations"
              arrow
            >
              <IconButton
                size="small"
                color="secondary"
                variant="contained"
                className={isRouteDisabled ? classes.iconButtonDisabled : classes.iconButton}
                onClick={() => {
                  handleRouteAllLocations(filterBooking)
                }}
                disabled={isRouteDisabled}
              >
                <AltRouteIcon
                  size="small"
                  className={isRouteDisabled ? classes.iconRouteDisabled : classes.iconRoute}
                />
              </IconButton>
            </Tooltip>
          )}
          <BookingInfo
            data={[
              {
                id: 1,
                icon: IMAGES.iconCurtain[color],
                info: total.curtain,
              },
              {
                id: 2,
                icon: IMAGES.iconLaundry[color],
                info: total.other,
              },
            ]}
          />
        </Stack>
      )}
    </Stack>
  )
}

export default observer(DriverType)
